import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';

// ── Real CSSC photos curated from all gallery folders ──────────────────────
const HERO_IMAGES = [
  { src: '/images/celebrity/celebrity_cover_new.jpg',    label: 'Celebrity Management' },
  { src: '/images/celebrity/celebrity_new_2.jpg',        label: 'Star Collaborations'  },
  { src: '/images/celebrity/Sharman GLS.jpeg',           label: 'Premium Events'       },
  { src: '/images/events/event_mi_meet_greet.jpg',       label: 'Meet & Greet'         },
  { src: '/images/events/event_hardik_mandira.jpg',      label: 'Sports & Stars'       },
  { src: '/images/events/Axis Bhajji Harsha_1756933332556.png', label: 'Cricket Icons' },
  { src: '/images/hottest-events/event_race.png',        label: 'Motorsport Experiences' },
  { src: '/images/hottest-events/ipl_2026.png',          label: 'IPL 2026'             },
  { src: '/images/hottest-events/lollapalooza.png',      label: 'Music Festivals'      },
  { src: '/images/hottest-events/event_1.png',           label: 'Live Events'          },
  { src: '/images/sports/sports_stadium_experience_2.jpg', label: 'Stadium Experiences' },
  { src: '/images/celebrity/celebrity7.png',             label: 'A-List Talent'        },
];

// Float columns: [col1Images, col2Images, col3Images]
const COLUMNS = [
  [0, 3, 8, 11],
  [1, 4, 6, 9],
  [2, 5, 7, 10],
];

// Stagger variant for initial load
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.3 },
  },
};

const textVariants = {
  hidden:  { opacity: 0, y: 60, filter: 'blur(8px)' },
  visible: { opacity: 1, y: 0,  filter: 'blur(0px)', transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

// A single floating column of images ─────────────────────────────────────────
const FloatingColumn = ({ indices, columnIndex }) => {
  // Different vertical speeds per column for organic parallax feel
  const durations = [28, 35, 22];
  const duration  = durations[columnIndex % durations.length];

  // Stack images twice so the loop is seamless
  const cards = [...indices, ...indices].map((imgIndex, i) => ({
    ...HERO_IMAGES[imgIndex],
    key: `col${columnIndex}-${i}`,
  }));

  return (
    <div className="overflow-hidden flex flex-col gap-4 h-full relative">
      <motion.div
        className="flex flex-col gap-4"
        animate={{ y: [0, -50 * indices.length * 2] }}
        transition={{
          duration,
          repeat: Infinity,
          ease: 'linear',
          repeatType: 'loop',
        }}
        style={{ willChange: 'transform' }}
      >
        {cards.map(({ src, label, key }) => (
          <div
            key={key}
            className="relative flex-shrink-0 rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
            style={{
              height: '180px',
              background: 'rgba(255,255,255,0.04)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <img
              src={src}
              alt={label}
              className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-500"
              loading="lazy"
              onError={(e) => {
                // Graceful fallback if image doesn't exist
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.parentElement) {
                  e.currentTarget.parentElement.style.background =
                    'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)';
                }
              }}
            />
            {/* Caption overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
              <p className="text-xs text-white/80 font-medium tracking-wide truncate">{label}</p>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
export const Hero = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  const texts = [
    { headline: "Where Stars\nMeet Success",     sub: 'Connecting brands with A-list celebrities and sporting legends.'                  },
    { headline: "Excellence In\nEvery Partnership", sub: 'Great events. Smart sponsorships. Happy clients. We keep it simple.'           },
    { headline: "Delivering Impact\nThat Matters",  sub: 'Zero drama. Just results that work. Your brand deserves it.'                   },
    { headline: "Your Vision,\nOur Expertise",      sub: 'Events, endorsements, experiences – we make every moment count.'              },
  ];

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const timer = setInterval(
      () => setCurrentTextIndex((prev) => (prev + 1) % texts.length),
      7000
    );
    return () => clearInterval(timer);
  }, [texts.length]);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: '#080a10' }}
    >
      {/* ── Ambient glow ─────────────────────────────────────────────────── */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-5%]  w-[500px] h-[500px] rounded-full bg-purple-700/10 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-indigo-800/5 blur-[150px]" />
      </div>

      {/* ── Right side: Floating photo columns ───────────────────────────── */}
      <div
        className="absolute right-0 top-0 h-full z-10 pointer-events-none hidden md:flex gap-4 items-start"
        style={{ width: '42%', maskImage: 'linear-gradient(to left, transparent 0%, black 30%, black 70%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to left, transparent 0%, black 30%, black 70%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)', WebkitMaskComposite: 'intersect', maskComposite: 'intersect' }}
      >
        {COLUMNS.map((indices, colIdx) => (
          <div key={colIdx} className="flex-1 h-full" style={{ marginTop: colIdx === 1 ? '-60px' : colIdx === 2 ? '-120px' : '0px' }}>
            <FloatingColumn indices={indices} columnIndex={colIdx} />
          </div>
        ))}
      </div>

      {/* ── Left side: Text content ──────────────────────────────────────── */}
      <div className="relative z-20 w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-32">
        <div className="max-w-xl xl:max-w-2xl space-y-10">

          {/* Brand label - Alternating Gold/Blue */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 backdrop-blur-md px-4 py-2 rounded-full"
          >
            <motion.span 
              className="w-2 h-2 rounded-full shadow-[0_0_8px_rgba(255,255,255,0.5)]" 
              animate={{ backgroundColor: currentTextIndex % 2 === 0 ? '#3B82F6' : '#F5C518', scale: [1, 1.5, 1], opacity: [0.7, 1, 0.7] }}
              transition={{ backgroundColor: { duration: 1 }, scale: { repeat: Infinity, duration: 2 }, opacity: { repeat: Infinity, duration: 2 } }}
            />
            <motion.span 
              className="text-xs font-bold tracking-[0.2em] uppercase"
              animate={{ color: currentTextIndex % 2 === 0 ? '#F5C518' : '#3B82F6' }}
              transition={{ duration: 1 }}
            >
              Centre Stage Sports &amp; Celebrities
            </motion.span>
          </motion.div>

          {/* Animated headline + sub */}
          <div className="space-y-6">
            <div className="min-h-[160px] md:min-h-[200px] flex items-start">
              <AnimatePresence mode="wait">
                <motion.h1
                  key={currentTextIndex}
                  initial={{ opacity: 0, y: 50, filter: 'blur(10px)' }}
                  animate={{ opacity: 1, y: 0,  filter: 'blur(0px)' }}
                  exit={{   opacity: 0, y: -40, filter: 'blur(10px)' }}
                  transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
                  className="text-5xl md:text-6xl xl:text-7xl font-extrabold text-white leading-[1.1] tracking-tight font-heading whitespace-pre-line"
                >
                  {texts[currentTextIndex].headline}
                </motion.h1>
              </AnimatePresence>
            </div>

            <div className="min-h-[60px] flex items-start">
              <AnimatePresence mode="wait">
                <motion.p
                  key={currentTextIndex + '-sub'}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0  }}
                  exit={{   opacity: 0, y: -10 }}
                  transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
                  className="text-base md:text-xl text-gray-400 max-w-md leading-relaxed"
                >
                  {texts[currentTextIndex].sub}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 pt-4 w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <button
              onClick={() => scrollToSection('celebrity')}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-black font-semibold text-base px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-gray-100 hover:scale-[1.02] active:scale-95 transition-all duration-300 shadow-[0_0_40px_rgba(255,255,255,0.15)]"
            >
              Explore Services
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
            </button>

            <button
              onClick={() => scrollToSection('contact')}
              className="w-full sm:w-auto inline-flex items-center justify-center font-semibold text-base px-6 sm:px-8 py-3 sm:py-4 rounded-full border border-white/15 text-white hover:border-white/40 hover:bg-white/8 backdrop-blur-sm hover:scale-[1.02] active:scale-95 transition-all duration-300"
            >
              Get in Touch
            </button>
          </motion.div>

          {/* Stats row */}
          <motion.div
            className="flex flex-wrap gap-8 pt-6 border-t border-white/8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
          >
            {[
              { value: '30+',  label: 'Years Exp.'      },
              { value: '10L+', label: 'Tickets Sold'    },
              { value: '300+', label: 'Celeb Partners'  },
            ].map(({ value, label }) => (
              <div key={label}>
                <p className="text-3xl font-bold text-white tabular-nums">{value}</p>
                <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider font-medium">{label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ── Scroll indicator ─────────────────────────────────────────────── */}
      <motion.div
        className="absolute bottom-8 left-8 z-20 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
      >
        <motion.div
          className="w-6 h-10 border border-white/20 rounded-full flex justify-center"
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
        >
          <motion.div
            className="w-1 h-2 bg-white/50 rounded-full mt-2"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ repeat: Infinity, duration: 2.2 }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};